import app from 'flarum/forum/app';
import Select from 'flarum/common/components/Select';
import Icon from 'flarum/common/components/Icon';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { field } from './Field';
import * as NickRules from '../utils/nickRules';
const t = (key, params) => app.translator.trans('ppk2-profile.forum.club_team.mini.' + key, params);
export const MAX_TEAMS = 10; // MiniTeamService::MAX_TEAMS_PER_PLAYER
const CREATE = 'create';

/**
 * Mini-teams part of the modal state. `list` is the ppk2MiniTeams attribute:
 * [{ id, name, role: captain|player, status, lastMember, pending?: [{userId, name}] }].
 * Every action here is IMMEDIATE (own API call), not part of «Сохранить».
 */
export function miniState(initial) {
  return {
    list: Array.isArray(initial) ? initial : [],
    teams: null,
    // GET /api/ppk2/teams?type=mini
    loadError: false,
    picking: false,
    creating: false,
    newName: '',
    showNameError: false,
    prompt: null,
    // team id whose last member is leaving -> «Удалить или оставить пустой?»
    busy: false
  };
}
export function loadMiniTeams(ms) {
  return app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/ppk2/teams',
    params: {
      type: 'mini'
    }
  }).then(response => {
    ms.teams = response.teams || [];
  }).catch(() => {
    ms.loadError = true;
  }).finally(() => m.redraw());
}
function nameError(name) {
  const n = NickRules.normalize(name);
  if (!n) return ['ppk2-profile.lib.mini.name_required', {}];
  const e = NickRules.check(n);
  return e ? ['ppk2-profile.lib.mini.name_' + e[0].substr(NickRules.KEY.length), e[1]] : null;
}

/** POST an action; refresh the chips, the cached attribute and the list of mini-teams. */
function act(ms, user, path, body, notice) {
  ms.busy = true;
  m.redraw();
  return app.request({
    method: 'POST',
    url: app.forum.attribute('apiUrl') + '/ppk2/mini-teams' + path,
    body: body || {}
  }).then(response => {
    ms.list = response.miniTeams || [];
    user.pushAttributes({
      ppk2MiniTeams: ms.list
    });
    app.alerts.show({
      type: 'success'
    }, notice(response.result || {}));
    ms.picking = false;
    ms.creating = false;
    ms.prompt = null;
    return loadMiniTeams(ms);
  }).catch(() => {}) // Flarum shows the server message (422 / 403)
  .finally(() => {
    ms.busy = false;
    m.redraw();
  });
}
function leave(ms, user, team, del) {
  return act(ms, user, '/' + team.id + '/leave', {
    delete: !!del
  }, r => r.deleted ? t('deleted', {
    name: r.name
  }) : t('left', {
    name: r.name
  }));
}
function chip(ms, user, team, disabled) {
  const pending = team.status !== 'confirmed';
  const captain = team.role === 'captain';
  return m("span", {
    className: 'Ppk2Chip' + (pending ? ' Ppk2Chip--pending' : ''),
    "data-tip": pending ? t('pending_tip') : null,
    tabindex: pending ? 0 : null
  }, captain ? m("span", {
    className: "Ppk2Chip-role Ppk2Chip-role--captain",
    title: app.translator.trans('ppk2-profile.forum.club_team.team_role.captain')
  }, m(Icon, {
    name: "fas fa-crown"
  })) : null, m("span", {
    className: "Ppk2Chip-name"
  }, team.name), pending ? m(Icon, {
    name: "far fa-clock",
    className: "Ppk2Chip-clock"
  }) : null, m("button", {
    type: "button",
    className: "Ppk2Chip-remove",
    disabled: disabled,
    title: t('remove'),
    "aria-label": t('remove'),
    onclick: () => team.lastMember ? ms.prompt = team.id : leave(ms, user, team, false)
  }, m(Icon, {
    name: "fas fa-xmark"
  })));
}
function picker(ms, user, disabled) {
  const mine = ms.list.map(x => x.id);
  const available = (ms.teams || []).filter(x => !mine.includes(x.id));
  const options = {
    '': t('choose')
  };
  available.forEach(x => options['m' + x.id] = x.name);
  options[CREATE] = t('create_new');
  return m("span", {
    className: "Ppk2MiniPicker"
  }, m(Select, {
    className: "Ppk2MiniPicker-input",
    name: "ppk2MiniPick",
    options: options,
    value: "",
    disabled: disabled,
    onchange: v => {
      if (v === CREATE) {
        ms.picking = false;
        ms.creating = true;
        ms.newName = '';
        ms.showNameError = false;
      } else if (v) {
        const team = available.find(x => 'm' + x.id === v);
        act(ms, user, '/' + team.id + '/join', {}, r => r.captain ? t('joined_captain', {
          name: r.name
        }) : t('joined', {
          name: r.name
        }));
      }
    }
  }), m("button", {
    type: "button",
    className: "Ppk2Chip-remove",
    title: app.translator.trans('ppk2-profile.forum.buttons.cancel'),
    onclick: () => ms.picking = false
  }, m(Icon, {
    name: "fas fa-xmark"
  })));
}
function createForm(ms, user, disabled) {
  const error = nameError(ms.newName);
  const show = error && (ms.newName !== '' || ms.showNameError);
  const submit = () => {
    if (error) {
      ms.showNameError = true;
      return;
    }
    act(ms, user, '', {
      name: NickRules.normalize(ms.newName)
    }, r => t('created', {
      name: r.name
    }));
  };
  return m("div", {
    className: "Ppk2MiniCreate"
  }, m("div", {
    className: "Ppk2MiniCreate-row"
  }, m("input", {
    className: 'FormControl' + (show ? ' Ppk2NickInput--invalid' : ''),
    name: "ppk2MiniNew",
    type: "text",
    maxlength: NickRules.MAX_LENGTH + 10,
    autocomplete: "off",
    spellcheck: "false",
    placeholder: t('create_placeholder'),
    value: ms.newName,
    disabled: disabled,
    oninput: e => ms.newName = e.target.value,
    onkeydown: e => {
      if (e.key === 'Enter') {
        e.preventDefault(); // do not submit the whole profile form
        submit();
      }
    }
  }), m(Button, {
    className: "Button Button--primary",
    disabled: disabled,
    loading: ms.busy,
    onclick: submit
  }, t('create_button')), m(Button, {
    className: "Button",
    disabled: ms.busy,
    onclick: () => ms.creating = false
  }, app.translator.trans('ppk2-profile.forum.buttons.cancel'))), show ? m("div", {
    className: "Ppk2NickStatus Ppk2NickStatus--invalid"
  }, m(Icon, {
    name: "fas fa-circle-xmark"
  }), " ", app.translator.trans(error[0], error[1])) : null);
}
function prompt(ms, user) {
  const team = ms.list.find(x => x.id === ms.prompt);
  if (!team) return null;
  return m("div", {
    className: "Ppk2Prompt"
  }, m("div", {
    className: "Ppk2Prompt-text"
  }, m(Icon, {
    name: "fas fa-circle-question"
  }), " ", t('last_prompt', {
    name: team.name
  })), m("div", {
    className: "Ppk2Prompt-buttons"
  }, m(Button, {
    className: "Button Ppk2Prompt-delete",
    icon: "fas fa-trash",
    disabled: ms.busy,
    onclick: () => leave(ms, user, team, true)
  }, t('delete')), m(Button, {
    className: "Button",
    disabled: ms.busy,
    onclick: () => leave(ms, user, team, false)
  }, t('keep_empty')), m(Button, {
    className: "Button Button--link",
    disabled: ms.busy,
    onclick: () => ms.prompt = null
  }, app.translator.trans('ppk2-profile.forum.buttons.cancel'))));
}
function pendingList(ms, user, disabled) {
  const rows = [];
  ms.list.forEach(team => (team.pending || []).forEach(p => rows.push(m("div", {
    className: "Ppk2Pending-row"
  }, m("span", {
    className: "Ppk2Pending-who"
  }, m(Icon, {
    name: "far fa-clock"
  }), " ", m("b", null, p.name), " \u2192 ", team.name), m(Button, {
    className: "Button Button--primary Ppk2Pending-confirm",
    icon: "fas fa-check",
    disabled: disabled,
    onclick: () => act(ms, user, '/' + team.id + '/confirm/' + p.userId, {}, () => t('confirmed', {
      name: p.name,
      team: team.name
    }))
  }, t('confirm'))))));
  return rows.length ? m("div", {
    className: "Ppk2Pending"
  }, m("div", {
    className: "Ppk2Pending-label"
  }, t('pending_label')), rows) : null;
}

/** Row «Мини-команды». */
export function miniSection(ms, user, loading) {
  const disabled = loading || ms.busy;
  let body;
  if (ms.loadError) body = m("div", {
    className: "Ppk2NickStatus Ppk2NickStatus--invalid"
  }, t('load_error'));else if (!ms.teams) body = m(LoadingIndicator, {
    display: "inline",
    size: "small"
  });else {
    const full = ms.list.length >= MAX_TEAMS;
    body = [m("div", {
      className: "Ppk2Chips"
    }, ms.list.map(team => chip(ms, user, team, disabled)), full ? m("span", {
      className: "Ppk2Muted"
    }, t('limit_reached', {
      max: MAX_TEAMS
    })) : ms.picking ? picker(ms, user, disabled) : ms.creating ? null : m(Button, {
      className: "Button Ppk2ChipAdd",
      icon: "fas fa-plus",
      disabled: disabled,
      onclick: () => ms.picking = true
    }, t('add'))), ms.creating && !full ? createForm(ms, user, disabled) : null, ms.prompt ? prompt(ms, user) : null, pendingList(ms, user, disabled)];
  }
  return field('mini', t('label'), body, null, t('tip'));
}
flarum.reg.add('ppk2-profile', 'forum/components/MiniTeamSection', { MAX_TEAMS: MAX_TEAMS,miniState: miniState,loadMiniTeams: loadMiniTeams,miniSection: miniSection, });